import QRCode from 'qrcode';
import type { CaseSummary } from '../types/case.types';

// Scan URL always goes through /qr/scan (logout + login), then /qr/cases (view).
// Include PUBLIC_URL so the QR works when the app is served under a sub-path
// (e.g. /rrms) as well as at the root.
export function buildQrContent(caseId: string): string {
  return `${window.location.origin}${process.env.PUBLIC_URL || ''}/qr/scan/${encodeURIComponent(caseId)}`;
}

// display_number (e.g. ADM-EST-26-0001) is the short, memorable label printed
// under the QR; older cases without one fall back to the system number.
type QrLabelCase = Pick<CaseSummary, 'rrms_ref_number' | 'cms_number' | 'case_id' | 'display_number'>;

// cms_number only exists for cases filed via the legacy CMS integration — the
// system-generated rrms_ref_number is always present and is what actually
// identifies a case end to end in this deployment.
export function getSystemCaseNumber(caseItem: Pick<CaseSummary, 'rrms_ref_number' | 'cms_number' | 'case_id'>): string {
  return caseItem.rrms_ref_number || caseItem.cms_number || caseItem.case_id;
}

/** Opens a small print-formatted window with an already-generated QR data URL and triggers print. */
export function openQrPrintWindow(qrDataUrl: string, caseItem: QrLabelCase): void {
  const systemCaseNumber = getSystemCaseNumber(caseItem);
  const displayNumber = caseItem.display_number || '';
  const printWindow = window.open('', '_blank', 'width=420,height=560');
  if (!printWindow) return;
  const title = displayNumber || systemCaseNumber;
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>QR Code — ${title}</title>
        <style>
          body { font-family: Arial, sans-serif; text-align: center; padding: 32px 16px; }
          img { width: 260px; height: 260px; }
          .case-no { margin-top: 16px; font-size: 18px; font-weight: 700; letter-spacing: 0.02em; }
          .display-no { margin-top: 12px; font-size: 26px; font-weight: 800; letter-spacing: 0.04em; }
          .label { margin-top: 2px; font-size: 11px; color: #666; text-transform: uppercase; letter-spacing: 0.08em; }
          .ref { margin-top: 6px; font-size: 11px; color: #666; letter-spacing: 0.04em; }
        </style>
      </head>
      <body>
        <img src="${qrDataUrl}" alt="Case QR code" />
        ${
          displayNumber
            ? `<div class="display-no">${displayNumber}</div>
               <div class="ref">Ref: ${systemCaseNumber}</div>`
            : `<div class="label">System Case Number</div>
               <div class="case-no">${systemCaseNumber}</div>`
        }
      </body>
    </html>
  `);
  printWindow.document.close();
  printWindow.focus();
  printWindow.onload = () => {
    printWindow.print();
  };
}

/** Generates the QR code for a case and immediately opens the print dialog for it. */
export async function printCaseQrCode(caseItem: CaseSummary): Promise<void> {
  const qrDataUrl = await QRCode.toDataURL(buildQrContent(caseItem.case_id), {
    errorCorrectionLevel: 'M',
    margin: 2,
    width: 280,
    color: {
      dark: '#1a1a2e',
      light: '#ffffff',
    },
  });
  openQrPrintWindow(qrDataUrl, caseItem);
}
